'use client'
'use client'

import { BuilderBlocks } from '@builder.io/react'
import { CheckIcon, MinusIcon, XMarkIcon } from '@heroicons/react/24/outline'

import { Container } from '@src/components/Container/Container'
import { FeaturedChartDefaults } from '@src/components/FeaturedChart/FeaturedChart.defaults'
import {
  type FeaturedChartProps,
  type FeaturedChartRow,
} from '@src/components/FeaturedChart/FeaturedChart.props'
import { cn } from '@src/utils/tailwind'

export const FeaturedChart = (props: FeaturedChartProps = FeaturedChartDefaults) => {
  const { builderBlock, rows, heading, rowHeadings, featuredColumnHeader, blocksBottom } = props

  const renderHeader = ({ onlyTwoColumns }: { onlyTwoColumns: boolean }) => {
    return (
      <tr className="h-[45px] lg:h-auto">
        <th
          className={cn('w-(--col-1-width)', {
            'invisible lg:visible': !onlyTwoColumns,
          })}
        />

        <th
          className={cn('relative z-10 w-(--col-2-width) text-center font-medium', {
            'invisible lg:visible': !onlyTwoColumns,
          })}
        >
          {featuredColumnHeader}
        </th>
        {rowHeadings?.map((header) => {
          return (
            <th
              className={cn(
                'w-(--col-3-width) text-center text-body-xs font-normal text-bodi-gray-disabled',
                {
                  'hidden lg:block': onlyTwoColumns,
                }
              )}
              key={header.text}
              dangerouslySetInnerHTML={{ __html: header.text ?? '' }}
            ></th>
          )
        })}
      </tr>
    )
  }

  const renderRow = (args: { row: FeaturedChartRow; i: number; onlyTwoColumns?: boolean }) => {
    const { i, row, onlyTwoColumns } = args

    return (
      <tr key={i} className="group relative bg-bodi-black-text odd:bg-[#1d1f20]">
        <td
          className={cn('p-4 text-bodi-white first:rounded-l-2xl', {
            'invisible lg:visible': !onlyTwoColumns,
          })}
        >
          <BuilderBlocks
            parentElementId={builderBlock?.id}
            dataPath={`component.rows.${i}`}
            blocks={row.blocks}
          />
        </td>
        <td
          className={cn('relative z-10 p-0', {
            'invisible lg:visible': !onlyTwoColumns,
          })}
        >
          <div className="mx-auto flex size-8 items-center justify-center rounded-full bg-bodi-white text-bodi-black-text">
            <CheckIcon className="size-5 stroke-[2.5px]" />
          </div>
          <div className="absolute bottom-0 left-0 h-px w-full bg-bodi-gray-dark/25 group-last-of-type:hidden"></div>
        </td>
        {row.content.map(({ icon }, i) => {
          const iconMap = {
            checkmark: CheckIcon,
            dash: MinusIcon,
            x: XMarkIcon,
          }
          const Icon = iconMap[icon]

          return (
            <td
              key={i}
              className={cn(
                'border-r border-bodi-gray-dark/50 p-0 last:rounded-r-2xl last:border-0 lg:w-auto',
                {
                  'hidden lg:block': onlyTwoColumns,
                }
              )}
            >
              <div
                className={cn('mx-auto flex size-8 items-center justify-center rounded-full', {
                  'bg-bodi-green-deep-500': icon === 'checkmark',
                })}
              >
                <Icon
                  className={cn('size-7 stroke-[2.5px] text-bodi-gray-medium', {
                    'text-bodi-white size-5': icon === 'checkmark',
                  })}
                />
              </div>
            </td>
          )
        })}
      </tr>
    )
  }

  const renderTable = () => {
    const tableClasses = 'table-fixed w-fit lg:w-full'
    return (
      <div
        className={cn(
          'relative -mr-4 mb-8 lg:mr-0',
          '[--col-1-width:146px] lg:[--col-1-width:377px]',
          '[--col-2-width:107px] lg:[--col-2-width:142px]',
          '[--col-3-width:94px] lg:[--col-3-width:auto]'
        )}
      >
        <div className="relative overflow-x-scroll lg:overflow-x-auto">
          <table className={tableClasses}>
            <thead>{renderHeader({ onlyTwoColumns: false })}</thead>
            <tbody>
              {rows?.map((row, i) =>
                renderRow({
                  row,
                  i,
                  onlyTwoColumns: false,
                })
              )}
            </tbody>
          </table>
        </div>
        <table
          className={cn(
            tableClasses,
            'absolute top-0 left-0 bg-bodi-black-text lg:hidden lg:table-auto'
          )}
        >
          <thead>{renderHeader({ onlyTwoColumns: true })}</thead>
          <tbody>
            {rows?.map((row, i) =>
              renderRow({
                row,
                i,
                onlyTwoColumns: true,
              })
            )}
          </tbody>
        </table>
        {/* gradient background */}
        <div className="absolute top-0 left-(--col-1-width) h-full w-(--col-2-width) rounded-2xl bg-linear-to-b from-bodi-green-bright-500 to-bodi-green-deep-500"></div>
      </div>
    )
  }

  return (
    <div className="bg-bodi-black-text py-12 lg:py-16">
      <Container>
        <h2 className="text-heading-display-2 mb-8 text-center text-bodi-gray-pearl">{heading}</h2>
        {renderTable()}
        <BuilderBlocks
          parentElementId={builderBlock?.id}
          dataPath="component.blockBottom"
          blocks={blocksBottom}
        />
      </Container>
    </div>
  )
}
